import { findCitationParent } from './text-filter'

/**
 * Best-effort label and anchor for the section a selection lives in.
 * Prefers the rendered DOM heading: `citation.paragraphTitle` is unreliable —
 * for commentaries it tends to carry the full law title, and only laws set it
 * to a real "§ N. ..." string.
 */
export function findSectionInfo(
  node: Node,
  citationContext?: { paragraphTitle?: string, title?: string },
): { title?: string, anchor?: string } {
  const startEl = node instanceof Element ? node : node.parentElement

  // A selection starting inside a heading belongs to THAT heading — the
  // preceding-heading walk would attribute it to the previous section.
  const containingHeading = startEl?.closest('h1, h2, h3, h4, h5, h6') ?? null
  const lastHeading = containingHeading ?? nearestPrecedingHeading(startEl, findCitationParent(node))
  const headingText = readableHeadingText(lastHeading)
  const anchor = lastHeading?.id || undefined

  if (headingText) {
    return { title: headingText, anchor }
  }

  const citationTitle = citationContext?.title?.trim()
  const paragraphTitle = citationContext?.paragraphTitle?.trim()

  if (
    isUsableSectionLabel(paragraphTitle)
    && paragraphTitle !== citationTitle
  ) {
    return { title: paragraphTitle, anchor }
  }

  if (isUsableSectionLabel(citationTitle)) {
    return { title: citationTitle, anchor }
  }

  return { title: undefined, anchor }
}

function nearestPrecedingHeading(target: Element | null, scope: Element | null): Element | null {
  if (!target) {
    return null
  }

  const headings = (scope ?? target.parentElement)?.querySelectorAll('h1, h2, h3, h4, h5, h6') ?? []
  let lastBefore: Element | null = null

  for (const heading of headings) {
    if (scope && findCitationParent(heading) !== scope) {
      continue
    }

    const pos = heading.compareDocumentPosition(target)

    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
      lastBefore = heading
    }
    else {
      break
    }
  }

  return lastBefore
}

/**
 * Law headings space their inline parts via CSS margins, so textContent (and
 * innerText) collapse "§ 13" + "Bortfall…" to "§ 13Bortfall…". Join text
 * nodes with spaces, then strip the artificial space before punctuation.
 */
function readableHeadingText(el: Element | null): string | undefined {
  if (!el) {
    return undefined
  }

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  const parts: string[] = []
  let node: Node | null = walker.nextNode()

  while (node) {
    const part = (node.textContent ?? '').replace(/\s+/g, ' ').trim()

    if (part) {
      parts.push(part)
    }

    node = walker.nextNode()
  }

  if (!parts.length) {
    return undefined
  }

  return parts.join(' ').replace(/\s+([.,;:!?])/g, '$1')
}

// Rejects leaked AKN paths (e.g. "/akn/no/act/lov/2005-05-20/28").
function isUsableSectionLabel(label: string | undefined | null): label is string {
  if (!label) {
    return false
  }

  const trimmed = label.trim()

  if (!trimmed) {
    return false
  }

  if (trimmed.startsWith('/akn') || trimmed.includes('/act/lov/')) {
    return false
  }

  return true
}
